<template>
  <div>
    <!-- Loan Assignment Dialog -->
    <LoanAssignmentDialog
      ref="loanAssignmentDialog"
      @closed="onAssignmentDialogClosed"
    />

    <v-dialog
      persistent
      no-click-animation
      transition="dialog-bottom-transition"
      v-model="dialog"
      fullscreen
      style="overflow-y: hidden"
    >
      <!-- Yapu Alert -->
      <v-alert type="error" v-if="yapuAlertState.show" class="ma-0 pa-2">
        <v-row align="center">
          <v-col class="pa-0 grow ml-3">
            {{ yapuAlertState.message || 'Unable to save Yapu assessment' }}
          </v-col>
          <v-col class="pa-0 d-flex shrink">
            <v-btn
              small
              depressed
              class="ma-0 mr-2"
              @click="
                tryYapuAgain = true;
                onSaveButtonClicked();
              "
            >
              Try Again
            </v-btn>
            <v-btn
              text
              small
              depressed
              class="ma-0 text-capitalize"
              @click="
                tryYapuAgain = false;
                onSaveButtonClicked();
              "
            >
              <span> Continue without Yapu Assessment</span>
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              @click="yapuAlertState.show = !yapuAlertState.show"
              text
              icon
              depressed
              small
              outlined
              class="mx-3"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Alert -->
      <v-alert
        dense
        tile
        dismissible
        v-if="alertState.show"
        :type="alertState.type"
        class="ma-0"
      >
        {{ alertState.message }}
        <v-btn
          class="ml-10"
          v-if="alertState.showAdjustmentBtn"
          @click="$refs.adjustmentDialog?.openDialog()"
          small
          >Show Policy Adjustments</v-btn
        >
      </v-alert>

      <v-card
        :disabled="loading"
        :loading="loading"
        style="overflow: hidden"
        tile
      >
        <v-container class="full-height pa-0" fluid>
          <v-row>
            <v-col md="3" class="grey lighten-4 pa-4">
              <!-- General Loan Details Section -->
              <GeneralLoanDetailsSection
                :loanDetails="loan"
                @updated="onGeneralLoanDetailsUpdated"
                ref="generalLoanDetailsComponent"
              />
            </v-col>
            <v-col class="pl-0">
              <div class="d-flex justify-space-between">
                <div class="d-flex pt-5 pl-5">
                  <v-select
                    outlined
                    label="Creation Type"
                    dense
                    class="mr-2"
                    small
                    :disabled="!!loan.id"
                    :items="['New', 'Consolidation', 'Top Up']"
                    background-color="white"
                    @change="onCreationTypeChanged"
                    v-model="loan.loanCreationType"
                  />
                  <v-autocomplete
                    outlined
                    label="Accredited Branch"
                    dense
                    :items="branches"
                    item-value="id"
                    item-text="branchName"
                    v-model="loan.accreditedBranchId"
                    background-color="white"
                  />
                </div>
                <div class="d-flex pt-5 pr-3">
                  <v-btn
                    @click="closeModal()"
                    depressed
                    text
                    outlined
                    v-if="
                      !['adjustments-requested'].includes(
                        loan.adjustmentStatus
                      ) && loan.loanProgress != 'waiver approved'
                    "
                    :disabled="loading"
                    class="text-capitalize mx-1 shadow"
                    >Cancel</v-btn
                  >
                  <v-btn
                    type="submit"
                    depressed
                    :disabled="loading || hasPreviouslyStarted"
                    v-if="!yapuAlertState.show"
                    color="primary"
                    @click="onSaveButtonClicked()"
                    class="text-capitalize mx-1 shadow"
                    >Save</v-btn
                  >
                </div>
              </div>
              <v-stepper v-model="step" flat>
                <v-stepper-header class="elevation-0">
                  <template v-for="(s, i) in steps">
                    <template
                      v-if="s.value !== 2 || (s.value === 2 && isConsolidation)"
                    >
                      <v-stepper-step
                        editable
                        edit-icon="mdi-pencil-box"
                        :key="`step-${i}`"
                        class="text-body-2"
                        :step="s.value"
                        >{{ s.title }}</v-stepper-step
                      >
                      <v-divider
                        v-if="i != steps.length - 1"
                        :key="`dividerForstep-${i}`"
                      />
                    </template>
                  </template>
                </v-stepper-header>
                <!-- Content for each stepper -->
                <v-stepper-items>
                  <!-- Initial Loan details step -->
                  <v-stepper-content :step="1">
                    <LoanDistributionDetails
                      :loanDetails="loan"
                      ref="loanDistributionsComponent"
                      style="
                        min-height: 400px;
                        overflow-x: hidden;
                        overflow-y: auto;
                      "
                      @updated="onLoanDistributionUpdated"
                    />

                    <v-divider class="my-6" />
                    <!-- Loan Purpose Section -->
                    <loanPurposeSection
                      ref="loanPurposeComponent"
                      :loan="loan"
                      :loanPurposeTypes="purposes"
                      @updated="onLoanPurposesUpdated"
                    />
                  </v-stepper-content>

                  <!-- Consolidation Details step -->
                  <v-stepper-content v-if="isConsolidation" :step="2">
                    <ConsolidationDetails
                      :loanDetails="loan"
                      @consolidationUpdated="onConsolidationUpdated"
                      ref="consolidationDetails"
                    />
                  </v-stepper-content>

                  <!-- Roll Up Items step -->
                  <v-stepper-content :step="3">
                    <RollUpDetails
                      :loanAmount="loanAmount"
                      :fees="loanFees"
                      @feeDetailsChanged="onFeesUpdated"
                      ref="rollUpDetails"
                    />
                  </v-stepper-content>

                  <v-stepper-content
                    v-if="this.useDsr || this.useNetworth"
                    :step="4"
                  >
                    <AffordabilityTest
                      ref="affordabilityTest"
                      @updated="onAffordabilityTestUpdated"
                      :distributions="
                        loan?.loanDistributions.filter(
                          (el) =>
                            el.distributionType === 'internal' &&
                            el.personType !== 'co-maker'
                        )
                      "
                    />
                  </v-stepper-content>

                  <!-- Roll Up Items step -->
                  <v-stepper-content v-if="useYapu" :step="5">
                    <YapuDataCollection :loan="loan" ref="yapuDataCollection" />
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Dialog to prompt users if they need a waiver request or need to edit loan -->
    <AdjustmentDialog
      ref="adjustmentDialog"
      :loan="loan"
      :disableActionButtons="true"
    />

    <!-- Waiver Details Dialog -->
    <WaiverDetailsDialog
      :loanDetails="loan"
      ref="waiverDetailsDialog"
      @proceed="onLoanWaiverProceed"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import { removeNullAndEmptyArrays } from '@/lib/helpers';

import PageMixin from '@/mixins/page.mixin';
import PaymentCalculationService from '@/modules/loanpro/services/payment-calculation.service';
import RollUpDetails from '@/modules/loanpro/components/loan-pro/WorkLoad/RollUp.vue';
import WaiverDetailsDialog from './WaiverDetailsDialog.vue';
import ConsolidationDetails from '@/modules/loanpro/components/loan-pro/WorkLoad/ConsolidationDetails.vue';
import AffordabilityTest from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/AffordabilityTest.vue';
import GeneralLoanDetailsSection from '@/modules/loanpro/components/loan-pro/WorkLoad/GeneralLoanDetailsSection.vue';
import LoanDistributionDetails from '@/modules/loanpro/components/loan-pro/WorkLoad/LoanDistributionDetails.vue';
import AccountManagementService from '@/modules/loanpro/services/account-management.service';
import { LoanPoolService } from '@/modules/loanpro/services/loan-pool.service';
import LoanPurposeSection from './LoanPurposeSection.vue';
import LoanAssignmentDialog from './LoanAssignmentDialog.vue';
import YapuDataCollection from '@/modules/loanpro/components/loan-pro/WorkLoad/YapuDataCollection.vue';
import { LoanProgressGrouping } from '@/modules/loanpro/services/constants';
import { ClimateRiskAssessmentIntegrationService } from '@/modules/integrations/services/climate-risk-assessment/climate-risk-assessment-integration.service';
import { IntegrationConfigService } from '@/modules/integrations/services/integration-config.service.js';
import AdjustmentDialog from '../../LoanFlow/CreditAssessment/AdjustmentDialog.vue';

const defaultLoanObject = {
  loanCategory: 'Member',
  loanTerms: {},
  loanPurposes: [],
  loanDistributions: [],
  loanProductDetails: {
    maximumAge: 1
  }
};

export default {
  mixins: [PageMixin],

  components: {
    RollUpDetails,
    AffordabilityTest,
    LoanAssignmentDialog,
    ConsolidationDetails,
    LoanPurposeSection,
    WaiverDetailsDialog,
    LoanDistributionDetails,
    GeneralLoanDetailsSection,
    YapuDataCollection,
    AdjustmentDialog
  },

  computed: {
    ...mapGetters('yapuModule', ['globalClimateRiskRatings']),
    ...mapGetters('loanSettingsModule', [
      'riskFactors',
      'liabilityTypes',
      'generalSettings'
    ]),

    /**
     * Get loan amount
     */
    loanAmount: function () {
      return (
        +(this.loan.totalInternalLoanAmount || 0) +
        +(this.loan.totalExternalLoanAmount || 0) +
        +(this.loan.totalConsolidatedAmount || 0)
      );
    },

    /**
     * Get loan purposes from settings
     */
    purposes: function () {
      return (
        this.riskFactors
          ?.find((r) => r.riskFactorName == 'Purpose')
          ?.subRiskFactors?.map((el) => ({
            code: el.subRiskFactorCode,
            name: el.subRiskFactorName
          })) || []
      );
    }
  },

  async mounted() {
    window.document.title = 'Loan-Pro | Loan  Application';

    this.steps = this.defaultSteps;
    this.climateRiskAssessmentService =
      new ClimateRiskAssessmentIntegrationService();
    const integrationConfigService = new IntegrationConfigService();
    const { data: integrationConfig } = await integrationConfigService.find();
    this.integrationConfig = integrationConfig;
  },

  data() {
    return {
      timeout: null,
      selectedLoanProduct: {},
      isConsolidation: false,
      dialog: false,
      step: null,
      account: null,
      loanFees: [],
      loading: false,
      loan: defaultLoanObject,

      previouslyStartedLoans: [],
      hasPreviouslyStarted: false,

      //loan product flags
      useDsr: false,
      useNetworth: false,
      useAdjustments: false,
      useRiskFactorScore: false,
      useYapu: false,

      affordabilityStep: { title: 'Affordability Test', value: 4 },
      yapuStep: { title: 'Yapu Data Collection', value: 5 },
      defaultSteps: [
        { title: 'Loan Distribution Details', value: 1 },
        { title: 'Loan Consolidation', value: 2 },
        { title: 'Roll Up Items', value: 3 }
      ],
      steps: [],

      alertState: {
        type: 'error',
        show: false,
        message: '',
        showAdjustmentBtn: false
      },

      yapuData: {},
      tryYapuAgain: true,
      yapuAlertState: {
        show: false,
        message: ''
      },

      componentsRefs: {
        generalLoanDetailsComponent: 1,
        loanDistributionsComponent: 1,
        consolidationDetails: 2,
        loanPurposeComponent: 1,
        affordabilityTest: 4,
        yapuDataCollection: 5
      },

      //configurations
      integrationConfig: null,

      //services
      climateRiskAssessmentService: null
    };
  },

  methods: {
    ...mapActions('yapuModule', ['findGlobalClimateRiskRating']),
    /**
     * Get all previously started loans on this account
     */
    async fetchPeviousStartedLoans() {
      if (this.loan.id) return;

      const loanService = new LoanPoolService();
      const { data: res } = await loanService.searchLoan({
        loanProgresses: LoanProgressGrouping.IN_PROGRESS,
        search: this.loan.mainAccountNumber
      });

      this.previouslyStartedLoans = res.data || [];
    },

    /**
     * Check if loan has already been and notify the user
     */
    checkIfLoanHasAlreadyBeenStarted() {
      this.alertState.show = false;

      const product = this.loan?.loanProductDetails;

      this.hasPreviouslyStarted = this.previouslyStartedLoans.some(
        (el) => el.loanProductDetails?.id == product?.id
      );

      if (this.hasPreviouslyStarted) {
        this.alertState.type = 'warning';
        this.alertState.show = true;
        this.alertState.message =
          'This account already has a loan in progress for this loan product. Select a new loan product OR delete the existing one.';
      }
    },

    /**
     * Check if waiver is needed
     *
     * @returns { Boolean } r
     */
    isWaiverNeeded() {
      const waiverTerms = {};
      const lessKeys = ['interest', 'shares'];

      this.loan.loanTermWaiver = {};
      this.loan.loanAmount = this.totalLoanAmount();

      // const loanManagementService = new LoanManagementService();
      // loanManagementService.setPolicyTerms(this.loan);
      const maxTerms = structuredClone(this.loan.maxTerms);

      //make a copy of loan object to only check waiver against the
      //summation of total internal and total consolidated amount.
      const copiedLoan = structuredClone(this.loan);
      copiedLoan.loanAmount =
        +this.loan.totalInternalLoanAmount +
        +(this.loan.totalConsolidatedAmount || 0);

      for (const key in maxTerms) {
        if (key == 'tenure') continue;

        if (lessKeys.includes(key) && +copiedLoan[key] < +maxTerms[key]) {
          waiverTerms[key] = maxTerms[key] - copiedLoan[key];
          continue;
        } else if (
          !lessKeys.includes(key) &&
          +copiedLoan[key] > +maxTerms[key]
        ) {
          waiverTerms[key] = copiedLoan[key] - maxTerms[key];
        }
      }

      //Check if waiver terms has change if a waiver was already applied
      //Otherwise set waiver terms if exist
      if (Object.keys(waiverTerms).length > 0) {
        this.loan.loanTermWaiver = waiverTerms;
        this.$refs.waiverDetailsDialog.openDialog(this.loan);
        return true;
      }

      return false;
    },

    /**
     * Return total loan amount without external amount
     *
     * @returns { Number }
     */
    totalLoanAmountWithoutExternal() {
      return (
        (+this.loan.totalInternalLoanAmount || 0) +
        +(this.loan.totalConsolidatedAmount || 0) +
        +(this.loan.totalRollUp || 0)
      );
    },

    /**
     * Return total loan amount without roll up amount
     *
     * @returns { Number }
     */
    totalLoanAmountWithoutRollUp() {
      return (
        +(this.loan.totalInternalLoanAmount || 0) +
        +(this.loan.totalExternalLoanAmount || 0) +
        +(this.loan.totalConsolidatedAmount || 0)
      );
    },

    /**
     * Return total loan amount
     *
     * @returns { Number }
     */
    totalLoanAmount() {
      return (
        +(this.loan.totalInternalLoanAmount || 0) +
        +(this.loan.totalExternalLoanAmount || 0) +
        +(this.loan.totalConsolidatedAmount || 0) +
        +(this.loan.totalRollUp || 0)
      );
    },

    /**
     * Calculate the repayment amount
     */
    calculatePayments() {
      this.steps = this.defaultSteps;

      const loanAmount = this.totalLoanAmountWithoutExternal();
      const excludedTypes = ['co-borrower', 'guarantor', 'co-maker'];
      const distributions =
        this.loan?.loanDistributions?.filter(
          (el) => !excludedTypes.includes(el.personType)
        ) || [];
      const totalInternal = distributions.filter(
        (el) => el.distributionType === 'internal'
      ).length;

      let totalExternalAmount = 0;
      let totalFrequencyPayment = 0;
      let internalCount = 0;

      //update loan purpose amount if there is only one.
      if (this.loan.loanPurposes?.length == 1) {
        const purpose = this.loan.loanPurposes[0];
        purpose.amount = this.totalLoanAmount();
        this.loan.loanPurposes.splice(0, 1, purpose);
      }

      distributions.forEach((d) => {
        const index = this.loan.loanDistributions.findIndex(
          (el) =>
            el.personId === d.personId &&
            el.distributionType === d.distributionType
        );
        const dist = this.loan.loanDistributions[index];

        if (dist.distributionType === 'internal') {
          dist.loanAmount = loanAmount;
          dist.interest = +this.loan.interest;

          //calculate payment
          const result = this.calculatePayment(loanAmount, dist.interest);
          const payment = +(result / totalInternal).toFixed(2) * totalInternal; //convert number to an evenly distributable number;

          const isPaymentValid = (v) => isNaN(v) || v == null || payment !== v;

          //set internal payment frequency if previous value is null or NaN

          const quotient = +payment / totalInternal;
          if (totalInternal > 1 && totalInternal === internalCount + 1) {
            dist.frequencyPayment = isPaymentValid(dist.frequencyPayment)
              ? payment - quotient * (totalInternal - 1)
              : dist.frequencyPayment;
          } else {
            dist.frequencyPayment = isPaymentValid(dist.frequencyPayment)
              ? quotient
              : dist.frequencyPayment;
          }

          if (internalCount === 0) {
            //use the first internal dist payment frequency as the entire loan payment frequency
            this.loan.internalFrequencyPayment = payment;

            //add payment frequency to total payment frequency amount
            totalFrequencyPayment += payment;
          }
          internalCount++;
        } else {
          totalExternalAmount += +dist.loanAmount;
          dist.frequencyPayment = this.calculatePayment(
            dist.loanAmount,
            +dist.interest || 0
          );
          totalFrequencyPayment += +dist.frequencyPayment;
        }
        this.loan.loanDistributions.splice(index, 1, dist);
      });

      this.loan.totalExternalLoanAmount = +totalExternalAmount;
      this.loan.frequencyPayment = totalFrequencyPayment;
      this.loan.externalFrequencyPayment =
        totalFrequencyPayment - this.loan.internalFrequencyPayment;

      //only proceed if the loan product details is set.
      if (!this.loan?.loanProductDetails) return;

      //set flags
      const {
        useDsr,
        useNetworth,
        useAdjustments,
        useRiskFactorScore,
        useYapu
      } = this.loan?.loanProductDetails ?? {};
      this.useDsr = useDsr;
      this.useNetworth = useNetworth;
      this.useAdjustments = useAdjustments;
      this.useRiskFactorScore = useRiskFactorScore;
      this.useYapu =
        useYapu &&
        !!this.integrationConfig?.climateRiskAssessmentSystems?.selected;

      const showAffordabilityTest = this.useDsr || this.useNetworth;

      //add loan amount and payment to affordability test
      //check if loan product require dsr and networth
      if (showAffordabilityTest) {
        this.steps = [...this.steps, this.affordabilityStep];

        //find the total payments for all external loans and distribution
        //them amoung the persons on the account
        const externalPaymentAmount =
          this.loan.loanDistributions
            .filter((el) => el.distributionType === 'external')
            .reduce((acc, cur) => (acc += cur.frequencyPayment), 0) || 0;
        const distributedExternalPaymentAmount =
          externalPaymentAmount / internalCount;

        this.loan.loanDistributions.forEach((el, i) => {
          if (
            el.personType !== 'borrower' ||
            el.distributionType === 'external'
          )
            return;

          const liabilities = el.affordabilityResults?.liabilities || [];
          const index = liabilities.findIndex((el) => el.ref == this.loan.ref);
          const monthlyAmount =
            el.frequencyPayment + distributedExternalPaymentAmount;
          const totalPayment = this.totalLoanAmount();
          const hint = 'Current loan';
          if (index > -1) {
            const d = {
              ...liabilities[index],
              hint,
              monthlyAmount,
              totalPayment
            };
            liabilities.splice(index, 1, d);
          } else {
            liabilities.push({
              hint,
              totalPayment,
              monthlyAmount,
              ref: this.loan.ref,
              accountName: 'Loan Payment',
              description: `${this.loan.loanProductDetails.loanProductName}`
            });
          }

          if (el.affordabilityResults) {
            el.affordabilityResults.liabilities = liabilities;
            el.affordabilityResults.totalDebts = liabilities.reduce(
              (a, c) => a + Number(c.monthlyAmount),
              0
            );
            el.affordabilityResults.totalLiabilities = liabilities.reduce(
              (a, c) => a + Number(c.totalPayment),
              0
            );
            this.loan.loanDistributions.splice(i, 1, el);
          }
        });
      }

      if (this.useYapu) {
        this.steps = [...this.steps, this.yapuStep];
      }

      this.step = this.step == 4 && !showAffordabilityTest ? 1 : this.step;
      this.step = this.step == 5 && !this.useYapu ? 1 : this.step;
    },

    /**
     * Set the loan fees associated with the loan product
     */
    setLoanFees() {
      const fees = [];
      const gct =
        this.generalSettings.gct > 0 ? this.generalSettings.gct / 100 : 1;
      const product = this.loan.loanProductDetails;
      const loanAmount = this.totalLoanAmountWithoutRollUp();

      const loanFees =
        structuredClone(product?.loanTypeDetails?.loanFees) || [];

      //add shares to roll up fees
      loanFees.push({
        active: true,
        loanFeeName: 'Shares Requirement',
        feePolicy: [{ min: 0, max: 0, flat: 0, percentage: this.loan.shares }]
      });

      //add service charge/ processing fee to roll up fees
      loanFees.push({
        active: true,
        loanFeeName: 'Processing Fee',
        feePolicy: product.serviceCharges || []
      });

      const previousLoanFees =
        structuredClone(this.loan.loanFeeDetails?.fees) || [];

      loanFees
        .filter((el) => el.active)
        .forEach((el) => {
          const fee = el.feePolicy.find(
            (e) =>
              (loanAmount >= e.min && loanAmount <= e.max) ||
              (el.feePolicy.length == 1 && loanAmount > e.min)
          );
          if (!fee || (!fee?.flat && !fee?.percentage)) return;

          //lookup current fee in previous feeDetails
          const previousFee = previousLoanFees.find(
            (e) => e.feeName === el.loanFeeName
          );

          //
          if (!previousFee && this.loan.id) return;

          const percentWithGct = el.attractGct
            ? +fee.percentage + +(fee.percentage * gct).toFixed(2)
            : fee.percentage;

          const variableAmount = (fee.percentage / 100) * this.loanAmount;
          const variableAmountWithGct =
            this.loanAmount * (percentWithGct / 100);
          const flatFee = +fee.flat;
          const flatFeeWithGct = el.attractGct
            ? fee.flat + +(fee.flat * gct).toFixed(2)
            : fee.flat;
          const totalFee = variableAmount + flatFee;
          const totalFeeWithGct = variableAmountWithGct + flatFeeWithGct;

          //calculate roll up amounts
          const prevRollUpPercentageAmount =
            ((previousFee?.rollUpPercentage || 0) / 100) * this.loanAmount || 0;
          const prevRollUpFlatAmount = previousFee?.rollUpAmount || 0;

          const totalRollUpAmount =
            prevRollUpPercentageAmount + prevRollUpFlatAmount;

          const feeDetails = {
            feeCode: el.loanFeeCode,
            feeName: el.loanFeeName,
            totalAmount: loanAmount,
            gctAtTheTime: gct,
            attractGct: !!el.attractGct,
            canBeAmortized: !!el.canBeAmortized,
            rollUpPercentage: previousFee?.rollUpPercentage || 0,
            rollUpAmount: prevRollUpFlatAmount,
            totalRollUpAmount: totalRollUpAmount,
            feeRatePercentageWithGct: percentWithGct,
            feeRate: +fee.percentage,
            flatFee,
            flatFeeWithGct,
            variableAmount,
            variableAmountWithGct,
            totalFee,
            totalFeeWithGct,
            totalGct: totalFeeWithGct - totalFee
          };

          fees.push(feeDetails);
        });

      this.loan.loanFeeDetails = this.loan?.loanFeeDetails || {};

      if (fees.length > 0) {
        this.$nextTick(() => {
          this.loanFees = fees;
        });
      }
    },

    /**
     * Calculate replayment amount given the amount and interest based on the selected loan product
     *
     * @param { Number } amount - loan amount
     * @param { Number } interest - interest rate
     * @returns { Number } repayment amount
     */
    calculatePayment(amount, interest) {
      const { frequencyPeriod, tenure } = this.loan;
      const term = PaymentCalculationService.getFrequencyTerm(
        frequencyPeriod,
        tenure
      );

      const frequencyInterest = interest / 100 / frequencyPeriod;

      const { payment } = PaymentCalculationService.calculateRePaymentAmount(
        +amount || 0,
        term,
        frequencyInterest,
        this.loan.loanProductDetails.monthlyPaymentCalculation,
        this.loan.loanProductDetails.loanProductSubType
      );

      return payment;
    },

    /**
     * Gets call when the afforadability test change the loan distribution details
     *
     * @param { Array<Object> } loanDistributions - the updated loan distributions
     */
    onAffordabilityTestUpdated(loanDistributions) {
      const allOtherDistributionType = this.loan.loanDistributions.filter(
        (el) => el.distributionType === 'external'
      );
      this.loan.loanDistributions = loanDistributions.concat(
        allOtherDistributionType
      );
      this.calculatePayments();
    },

    /**
     * Gets call when the creation type change
     * It gets the prevoius loans if the creation type is topup or consolidation
     */
    async onCreationTypeChanged() {
      this.isConsolidation =
        this.loan.loanCreationType?.toLowerCase() !== 'new';

      if (this.isConsolidation) {
        this.steps[1].title = 'Loan Consolidation';
        if (this.loan.loanCreationType?.toLowerCase() === 'top up') {
          this.steps[1].title = 'Loan Top-up';
        }
        clearTimeout(this.timeout);
        this.timeout = setTimeout(async () => {
          await this.$refs.consolidationDetails.getPreviousLoans();
        }, 500);
      } else {
        this.step = this.step == 2 ? 1 : this.step;
      }
    },

    /**
     * Gets call when the loan purposes are updated
     *
     * @param { Array<Object> } loanPurposes - the updated loan purposes
     */
    onLoanPurposesUpdated(loanPurposes) {
      this.loan.loanPurposes = loanPurposes;
    },

    /**
     * Gets call when the loan distribution info is updated
     *
     * @param { Array<Object> } updatedLoan - the updated loan details
     */
    onLoanDistributionUpdated(updateLoan) {
      this.loan = {
        ...this.loan,
        ...updateLoan
      };
      this.calculatePayments();
      this.setLoanFees();
    },

    /**
     * Gets call when the general loan details are updated
     *
     * @param { { term: String, loan: Object }} Object - the updated loan details
     */
    async onGeneralLoanDetailsUpdated({ term, loan }) {
      this.loan = {
        ...this.loan,
        ...loan
      };

      if (term == 'product') {
        await this.onCreationTypeChanged();

        this.checkIfLoanHasAlreadyBeenStarted();
      }

      this.calculatePayments();
      this.setLoanFees();
    },

    /**
     * Gets call when the roll up component updates the fee details
     *
     * @param { Object} feeDetails - the updated fee details
     */
    onFeesUpdated(feeDetails) {
      this.loan.loanFeeDetails = {
        ...feeDetails
      };

      this.loan.totalRollUp =
        (feeDetails.rollupVariableAmountTotal || 0) +
        (feeDetails?.rollupFlatAmountTotal || 0);

      this.calculatePayments();
    },

    /**
     * Gets call when loans are selected or updated in the consolidation component
     *
     * @param { { loans: Array<Object>, totalConsolidatedAmount: Number } } event - the selected loans details
     */
    onConsolidationUpdated(event) {
      this.loan.loansToConsolidate = event.loans;
      this.loan.totalConsolidatedAmount = event.totalConsolidatedAmount;
      this.calculatePayments();
    },

    /**
     * Gets call when assignment dialog is closed
     */
    onAssignmentDialogClosed() {
      this.closeModal();
      clearTimeout(this.timeout);
      window.location.reload();
    },

    /**
     * Gets call when the user selects to proceed with the loan waiver
     */
    async onLoanWaiverProceed() {
      await this.saveLoan();
    },

    /**
     * Gets call when the user selects save button
     */
    async onSaveButtonClicked() {
      this.alertState.show = false;

      for (const componentKey in this.componentsRefs) {
        if (!this.$refs[componentKey]) continue;
        const { valid, message } = await this.$refs[componentKey]?.validate();
        if (!valid) {
          this.step = this.componentsRefs[componentKey];
          this.alertState.type = 'error';
          this.alertState.show = true;
          this.alertState.message = message;
          console.error(`[Validation Error]: `, message);
          return;
        }
      }

      if (this.loan.loanProductDetails.useYapu && this.tryYapuAgain) {
        const yapuData = await this.saveYapuResults();
        if (!yapuData) return;

        this.loan.yapuData = yapuData;
      }

      if (this.isWaiverNeeded()) return;

      await this.saveLoan();
    },

    /**
     * Closes the modal
     */
    async closeModal() {
      this.yapuAlertState.show = false;
      this.alertState.show = false;
      this.dialog = false;

      /**
       * Triggerd when the component is closed
       *
       * @event closed
       */
      this.$emit('closed');
    },

    /**
     * Clean up loan object for saving by removing unwanted properties
     */
    cleanUpLoanBeforeSaving(loan) {
      delete loan.loanProductDetails.checklist;
      delete loan.loanProductDetails.checklistRef;
    },

    /**
     * Generate and return yapu assessment based on the info entered in the yapu component section
     *
     * @returns { Object | False } yapu assessment results
     */
    async saveYapuResults() {
      this.yapuAlertState.show = false;
      const yapuData = this.$refs.yapuDataCollection.getAssessmentData();
      try {
        const { data: apiRes } =
          await this.climateRiskAssessmentService.getAssessmentResults(
            yapuData.yapuRequest
          );

        if (!apiRes.result) {
          this.yapuAlertState.show = true;
          this.yapuAlertState.message = apiRes.msg;
          return false;
        }

        const yapuResults = apiRes.data;

        if (
          yapuResults &&
          yapuResults.yapu_result &&
          yapuResults.yapu_result.length > 0
        ) {
          yapuData.yapuResults = yapuResults;

          if (yapuResults.yapu_result && yapuResults.yapu_result[1]) {
            this.loan.yapuVulnerabilityScore = `${
              Number(yapuResults.yapu_result[1].globalNetClimateRisk) * 100
            }`;
            this.loan.yapuGlobalRating = await this.findGlobalClimateRiskRating(
              this.loan.yapuVulnerabilityScore,
              this.globalClimateRiskRatings
            );
          }
        }

        return yapuData;
      } catch (e) {
        this.yapuAlertState.show = true;
        this.httpErrorHandler(e, { title: 'Unable to save YAPU application' });
        return false;
      }
    },

    /**
     * Send request to save loan details.
     */
    async saveLoan() {
      this.loading = true;
      this.alertState.show = true;
      this.alertState.type = 'info';
      this.alertState.message = 'Saving...';

      if (!this.loan.accreditedBranchId) {
        this.loading = false;
        this.alertState.show = true;
        this.alertState.type = 'error';
        this.alertState.message = 'Please select an accredited branch...';
        return;
      }

      try {
        const feeDetails = this.$refs.rollUpDetails?.getFeeDetails();

        let loan = structuredClone(this.loan);

        if (feeDetails && feeDetails?.fees?.length) {
          loan.loanFeeDetails = {
            ...feeDetails
          };
        }

        if (loan.adjustmentStatus === 'adjustments-requested') {
          loan.adjustmentStatus = 'adjustments-applied';
        }

        //Take into account the waiver that was applied
        for (const k in loan.loanTermWaiver) {
          const term = this.loan.previousTermWaiver?.[k];
          if (!term) continue;

          loan.loanTermWaiver[k] += Number(term);
        }

        loan.isTest = !!loan.isTest;
        loan.loanAmount = this.totalLoanAmount();
        const isObjectEmpty = (o) =>
          o && !!Object.keys(o).filter((k) => o[k] && o[k].length).length;

        this.cleanUpLoanBeforeSaving(loan);

        const loanService = new LoanPoolService();
        const savedLoanResponse = await (loan.id
          ? loanService.updateLoan(loan)
          : loanService.createLoan(loan));

        let index = 0;
        for (const d of loan.loanDistributions) {
          if (isObjectEmpty(d.affordabilityResults)) {
            const { data } = await loanService.saveAffordabilityTest(
              savedLoanResponse.data?.id,
              d.personId,
              d.affordabilityResults
            );
            loan.loanDistributions.splice(index, 1, {
              ...savedLoanResponse.data.loanDistributions[index],
              affordabilityResults: data
            });
          }
          index++;
        }

        const isCreating = this.loan.id ? false : true;
        loan = {
          ...savedLoanResponse.data,
          loanDistributions: this.loan.loanDistributions
        };
        this.alertState.show = false;

        if (isCreating) {
          this.$refs.loanAssignmentDialog.openDialog(loan);
        }

        this.closeModal();

        /**
         * Triggered when the loan details have been saved successfully
         *
         * @event save
         * @emit saved loan details
         */
        this.$emit('save', loan);
      } catch (e) {
        this.alertState.type = 'error';
        this.alertState.message = `${e.message}`;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Setup component for a new loan application.
     *
     * @param { Object } accountData - account object
     */
    async newApplication(accountData) {
      try {
        this.step = 1;
        this.loading = true;
        this.dialog = true;
        const accountId =
          accountData?.id ||
          accountData.account.id ||
          accountData.mainAccountNumber;
        const accountManagementService = new AccountManagementService();
        const { data } = await accountManagementService.getAccountById(
          accountId
        );

        this.loan.vettingOfficerId = this.user?.id;
        this.loan.processedBranchId = this.user?.branch?.id;

        await this.fetchPeviousStartedLoans();

        //Find previous affordabilityResults and set loanDistributions
        const loanService = new LoanPoolService();
        const personAffordabilityMap = {};
        for (const person of data.persons) {
          //set default values;
          personAffordabilityMap[person.id] = {
            assets: [],
            incomes: [],
            expenses: [],
            deductions: [],
            liabilities: []
          };

          const { data } = await loanService.findPreviousAffordabilityTest(
            person.id
          );
          if (data) personAffordabilityMap[person.id] = data;
        }

        this.loan.loanDistributions = data.persons.map((el) => ({
          personType: 'borrower',
          distributionType: 'internal',
          personId: el.id,
          person: el,
          affordabilityResults: personAffordabilityMap[el.id]
        }));

        const mainBranch = this.branches.find((el) => el.isMain);
        this.loan.ref = this.loan.ref || uuidv4();
        this.loan.mainAccountNumber = data.account.mainAccountNumber;
        this.loan.loanCreationType = this.loan.loanCreationType || 'New';
        this.loan.accreditedBranchId =
          this.loan.accreditedBranchId || mainBranch?.id;
        this.loan.maxTerms = {
          interest: 0,
          shares: 0,
          collateralValueUsed: 0,
          frequencyTenure: 0,
          loanAmount: 0
        };

        await this.onCreationTypeChanged();
      } catch (e) {
        this.httpErrorHandler(e, { title: 'Unable to open loan application' });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Handles the component alerts based on the status of the loan when called.
     */
    showInfoMessages() {
      this.alertState.showAdjustmentBtn = false;
      const missingDsrRatios = this.loan?.loanDistributions.some(
        (el) => !el.affordabilityResults?.incomes?.length
      );

      if (missingDsrRatios && this.loan.id) {
        this.alertState.message =
          'Affordability test required for additional signee(s) to the loan. Please review affordability test and save.';
        this.alertState.type = 'warning';
        this.alertState.show = true;
      }

      if (this.loan.adjustmentStatus === 'adjustments-requested') {
        this.alertState.message =
          'Loan terms have been adjusted due to risk based lending. Please review loan details and save.';
        this.alertState.showAdjustmentBtn = true;
        this.alertState.type = 'info';
        this.alertState.show = true;
      }

      if (this.loan.loanProgress === 'waiver approved') {
        this.alertState.message =
          'Loan waiver has been APPROVED. Please review and save loan application to proceed.';
        this.alertState.type = 'info';
        this.alertState.show = true;
      }

      if (this.loan.loanProgress === 'waiver rejected') {
        this.alertState.message =
          'Loan waiver has been REJECTED. Please review and save loan application to proceed.';
        this.alertState.type = 'warning';
        this.alertState.show = true;
      }
    },

    /**
     * Setup component to edit a loan application.
     *
     * @param { Object | string } loanIdOrLoan - the loan object or loan id
     * @param { Boolean } removeNull - indicate if all null values on the loan object should be removed
     */
    async editApplication(loanIdOrLoan, removeNull = true) {
      try {
        this.step = 1;
        this.loading = true;
        if (
          typeof loanIdOrLoan === 'string' ||
          loanIdOrLoan instanceof String
        ) {
          const loanService = new LoanPoolService();
          const { data } = await loanService.getLoanById(loanIdOrLoan);
          loanIdOrLoan = data;
        }

        this.loan = removeNull
          ? removeNullAndEmptyArrays(loanIdOrLoan)
          : loanIdOrLoan;

        this.showInfoMessages();

        this.dialog = true;
        clearTimeout(this.timeout);
        this.timeout = setTimeout(async () => {
          this.$refs.generalLoanDetailsComponent.calculateTerms();
          this.setLoanFees();
          await this.onCreationTypeChanged();

          if (this.loan?.loanIntegration?.yapuData) {
            this.$refs.yapuDataCollection?.load(
              this.loan.id,
              this.loan.loanIntegration?.yapuData?.questions,
              this.loan.yapuData?.loanIntegration?.coordinates
            );
          }
        }, 500);
      } catch (e) {
        this.httpErrorHandler(e, { title: 'Unable to open loan application' });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
